import React, { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router';

import { useAppSelector } from '@/store';
import { authStore } from '@/store/auth';
import { pages } from '@/settings/pages';
import { enabledFeatureFlagsApi } from '@/services/api';
import { auth } from '@/constants';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import i18n from '@/settings/i18n';

type Props = {
  memberAccessible?: boolean;
};

export const PrivateRoute: React.FC<Props> = ({ memberAccessible = false }) => {
  const isLoggedIn = useAppSelector(authStore.selectors.isLoggedInSelector);
  const hasSession = useAppSelector(authStore.selectors.hasSessionSelector);
  const isMember = useAppSelector(authStore.selectors.isMemberSelector);
  const gloplaId = useAppSelector(authStore.selectors.gloplaIdSelector);
  const navigate = useNavigate();
  const location = useLocation();
  const { flashError } = useFlashMessenger();

  const featchEnabledFeatureFlagsApi = useCallback(() => {
    enabledFeatureFlagsApi
      .getDetail('organization_select_transfer')
      .then((res) => {
        // FeatureFlagが有効な場合は組織選択画面へ遷移
        if (res.enabled) {
          return navigate(pages.organizations, { replace: true });
        }
      })
      .catch(() => {
        flashError(i18n.t('ui.error.unexpected'));
      });
  }, [flashError, navigate]);

  useEffect(() => {
    // ログイン中で組織IDが選択されてない場合、組織選択画面へ遷移（組織画面にいる場合はスキップ）
    const needSelectOrganization =
      isLoggedIn && !gloplaId && !(location.pathname === pages.organizations);

    if (needSelectOrganization) {
      return featchEnabledFeatureFlagsApi();
    }

    if (!hasSession) {
      return navigate(pages.signin.index, { replace: true });
    }

    const from = location.state?.from ?? { pathname: pages.top };

    if (!isLoggedIn) {
      window.location.href = auth.Url.signinUri;
    }

    // 組織選択あるけど権限での制限があるときはリダイレクト
    if (!memberAccessible && isMember) {
      return navigate(pages.top, { replace: true });
    }

    // 所属組織が1つの時に遷移元があればリダイレクト
    if (from.pathname !== pages.top) {
      return navigate(from.pathname, { replace: true });
    }
  }, [
    isLoggedIn,
    hasSession,
    isMember,
    location,
    memberAccessible,
    navigate,
    gloplaId,
    featchEnabledFeatureFlagsApi,
  ]);

  return <Outlet />;
};
